'use client'
import { memo, useMemo } from 'react'
import type { FC, Dispatch, SetStateAction } from 'react'
import { usePathname } from 'next/navigation'
import styled, { css } from 'styled-components'
import { MODALS } from '@/context'
import { useTranslator, useSession, useModal } from '@/hooks'
import { useCart } from '@/context/project/CartContext'
import { DY_GUEST_CART_ALLOWED, getDateFormatted, getDayOfWeekName, getDynamicText, getPathnameType, LANG } from '@/utils'
import { E_CART_DELIVERY_TYPE, E_DELIVERY_TYPE, type TypeCmsProduct, type TypeCmsVariant } from '@/schema'
import { createUnderline, mq, presets, vw } from '@/styles'
import { Link } from '@/components'
import { Price } from '../Price'
import { ButtonAddToCart } from '@/components/project/cart/ButtonAddToCart'
import { ToolTip } from './ToolTip'
import { MaxHeightContainer } from './max-height-container'
import { ImageSlider } from './ImageSlider'
import { RadioCheckBoxes } from '@/components/base/inputs'
import { ButtonAddPerBox } from '@/components/project/cart/ButtonAddPerBox'
import { FILTER_LABELS, Tags } from '../Tags'
import { ButtonTriggerCartType, getCartTypeFromLines, checkIfVariantPurchasable } from '../VariantCard'
import { getDeliveryTypeFallback, type TypeValidDeliveryTypeMessages } from '../../cart/CartOverview'
import { DivAddToCartWrapper } from '../../cart/DivAddToCartWrapper'
import { StockWarning } from '../StockWarning'

type Props = {
  data: TypeCmsProduct
  isPack?: boolean
  currentVariant: TypeCmsVariant
  setCurrentVariant: Dispatch<SetStateAction<TypeCmsVariant>>
  isPrivate?: boolean
}

const getAvailableVariants = (
  variants: TypeCmsVariant[],
  currentVariant: TypeCmsVariant,
  setCurrentVariant: Dispatch<SetStateAction<TypeCmsVariant>>
) =>
  variants?.map((variant) => ({
    variant,
    currentVariantId: currentVariant?.bigcommerceId,
    setCurrentVariant,
    text: variant.name,
  }))

export const DetailHeader: FC<Props> = memo(({ data, isPack = false, currentVariant, setCurrentVariant, isPrivate = false }) => {
  const { name, brand, description, categories } = data || {}
  const {
    media,
    name: variantName,
    price,
    stock,
    minSaleUnits,
    deliveryType,
    deliveryDate,
    onDemandDay,
    product,
    qtyPerBox,
		sku,
		stockWarning,
  } = useMemo(() => currentVariant, [currentVariant])
  const { inventoryTracking } = product || {}
  const { updatePayload: updateCartTypePayload } = useModal(MODALS.CART_TYPE)
  const isFrozen = deliveryType === E_DELIVERY_TYPE.FROZEN
  const { t } = useTranslator()
  const pathname = usePathname()
  const { user } = useSession()
  const { cartLines } = useCart()

  const cartType = useMemo(() => getCartTypeFromLines(cartLines), [cartLines])
  const isVariantPurchasable = checkIfVariantPurchasable({ userLoggedIn: !!user, deliveryType, stock, minSaleUnits })

  const displayAddPerBox = useMemo(() => qtyPerBox > 1 && minSaleUnits !== qtyPerBox, [qtyPerBox, minSaleUnits])

  const privateStore = useMemo(() => getPathnameType(pathname) === 'private', [pathname])

  const availableVariants = getAvailableVariants(data.variants, currentVariant, setCurrentVariant)

  return (
    <Header $frozen={isFrozen} data-sku={sku} data-frozen={isFrozen}>
      {!isPack && brand && (
        <Brand $frozen={isFrozen}>
          <Link
            href={
              isPrivate
                ? `/shop/brand/${brand.slug[LANG]}`
                : `/catalog/brand/${brand.slug[LANG]}`
            }
          >
            {brand.name}
          </Link>
        </Brand>
      )}
      <h1>{name}</h1>
			<span className='sku'>{sku}</span>
      <MediaWrapper>
				<Tags labels={product?.labels} filters={product?.filters} frozen={isFrozen} discount={price?.discount} />
        {media && (
          <ImageSlider
            data={media}
            isPack={isPack}
            key={currentVariant?.id}
          />
        )}
      </MediaWrapper>
      {!isPack && (
        <ProductInfo
          className={availableVariants?.length === 1 ? 'single-variant' : ''}
          $frozen={isFrozen}
        >
          {availableVariants?.length === 1 ? (
            <span className='variant-name'>{variantName}</span>
          ) : (
            <RadioCheckBoxes data={availableVariants} />
          )}

          {minSaleUnits > 1 && isPrivate && (
            <span>
              {getDynamicText(
                t('project.product.description_min_sale_units') as string,
                { minSaleUnits }
              )}
            </span>
          )}
        </ProductInfo>
      )}
      <FlexWrap $isPrivate={isPrivate}>
        {price && <Price data={price} />}
        <DivAddToCartWrapper>
					{((!!user || DY_GUEST_CART_ALLOWED) && deliveryType !== E_DELIVERY_TYPE.ON_DEMAND) && (
            <StockWarning data={{ stock, minSaleUnits, stockWarning, frozen: deliveryType === E_DELIVERY_TYPE.FROZEN }} displayedIn='pdp'/>
          )}
          {isVariantPurchasable && <>
            {cartLines?.length > 0 && ((cartType !== E_CART_DELIVERY_TYPE.FROZEN && deliveryType === E_DELIVERY_TYPE.FROZEN) || (cartType === E_CART_DELIVERY_TYPE.FROZEN && deliveryType !== E_DELIVERY_TYPE.FROZEN)) ? <ButtonTriggerCartType onClick={() => { updateCartTypePayload({ open: true, payload: { data: { cartType }}}) }} data-cart-type-trigger $frozen={cartType === E_CART_DELIVERY_TYPE.FROZEN}>i</ButtonTriggerCartType>
            : <>
              <ButtonAddToCart
                variant={currentVariant}
                frozen={isFrozen}
                showTextWhenNotInCart
                horizontal
              />
              {displayAddPerBox && <ButtonAddPerBox data={{
                name,
                brand,
                categories,
                variant: currentVariant,
                product,
                stock,
                inventoryTracking,
              }} />}
            </>}
          </>}
        </DivAddToCartWrapper>
      </FlexWrap>
      {(isPrivate && privateStore && deliveryDate && isVariantPurchasable) && (
        <SpanDeliveryDate>
          {getDynamicText(
            t(`project.product.delivery_date_${getDeliveryTypeFallback(deliveryType)}` as TypeValidDeliveryTypeMessages) as string,
            {
              deliveryDate: getDateFormatted(LANG, deliveryDate),
              onDemandDay: getDayOfWeekName(LANG, onDemandDay),
              deliveryHour: deliveryType === E_DELIVERY_TYPE.ON_DEMAND ? '14h' : '21h',
            }
          )}
        </SpanDeliveryDate>
      )}
      <MaxHeightContainer
        force_link
        className='description-clamp'
        elipsis={t('project.product.product_main.see_more') as string}
        data={description}
      />
    </Header>
  )
})

const Header = styled.header<{ $frozen: boolean }>`
	color: var(--color-text);

	&[data-frozen='true'] {
		--color-text: var(--color-card-text--frozen);

		~ section {
			--color-text: var(--color-card-text--frozen);
			--color-text--darker: var(--color-card-text--darker--frozen);
		}
	}

  h1 {
    ${presets.font30}
    color: ${({ $frozen }) =>
      $frozen ? 'var(--color-card-text--frozen)' : 'var(--color-text)'};
    margin-bottom: ${vw(19, 'mobile')};
    text-align: left;

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(19, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: 0;
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 0;
    }
  }

	.sku {
		font-size: 15px;
		font-weight: 300;
		letter-spacing: 1.2px;
		opacity: .75;
	}

  .description-clamp {
    ${presets.font18_3}
    color: ${({ $frozen }) =>
      $frozen ? 'var(--color-card-text--frozen)' : 'var(--color-text)'};
    height: ${vw(174, 'mobile')};
    margin: ${vw(40, 'mobile')} 0 ${vw(60, 'mobile')};
    overflow: hidden;

    ${mq.greaterThan('nexus7')} {
      height: ${vw(164, 'tablet')};
      margin: ${vw(40, 'tablet')} 0 ${vw(60, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      ${presets.font20_2}
      height: ${vw(230, 'desktop')};
      margin: ${vw(40, 'desktop')} 0 ${vw(80, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 230px;
      margin: 40px 0 80px;
    }

    .dy-max-button {
      ${presets.font18_3}
      ${createUnderline({ reverse: true, color: '--color-text' })}
      color: ${({ $frozen }) =>
        $frozen ? 'var(--color-card-text--frozen)' : 'var(--color-text)'};
      opacity: 1;
      transition: 200ms opacity ease-in-out;

      &:hover {
        opacity: 1;
      }

      ${mq.greaterThan('tablet')} {
        ${presets.font20_2}
      }
    }
  }

  .last_units,
  .on_demand {
    ${presets.font15}
    font-weight: 500;
    color: var(--color-gold);
    display: block;
    margin-top: ${vw(15, 'mobile')};
    text-align: center;

    ${mq.greaterThan('nexus7')} {
      margin-top: ${vw(15, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-top: ${vw(15, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-top: 15px;
    }
  }
`

const Brand = styled.span<{ $frozen: boolean }>`
  ${presets.font18}

  ${({ $frozen }) => $frozen ? css`
    ${createUnderline({ height: 0.5, color: 'frozen' })}
    color: var(--color-card-text--frozen);
  ` : css`
    ${createUnderline({ height: 0.5, color: 'red' })}
    color: var(--color-text);
  `}

  margin-bottom: ${vw(20, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    margin-bottom: ${vw(20, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    margin-bottom: ${vw(10, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin-bottom: 10px;
  }
`

const ProductInfo = styled.div<{ $frozen: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: ${vw(10, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    margin-bottom: ${vw(10, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    margin-bottom: ${vw(10, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    margin-bottom: 10px;
  }

  &.single-variant {
    display: block;
    max-width: 80%;

    .variant-name {
      ${presets.font18()}
    }
  }

  .variant-name {
    ${presets.font16()}
    display: block;
    font-weight: 500;
		margin-bottom: 5px;
  }

  div + span {
    margin-top: ${vw(30, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-top: ${vw(30, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-top: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-top: 30px;
    }
  }

  div {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin: 0 ${vw(15, 'mobile')};
    position: relative;

    ${mq.greaterThan('nexus7')} {
      margin: 0 ${vw(15, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin: 0 ${vw(15, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin: 0 15px;
    }

    > div {
      margin: 0 ${vw(15, 'mobile')} ${vw(10, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        margin: 0 ${vw(15, 'tablet')} ${vw(10, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        margin: 0 ${vw(15, 'desktop')} ${vw(10, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin: 0 15px 10px;
      }
    }
  }

  input {
    height: 100%;
    margin: 0;
    opacity: 0;
    position: absolute;
    width: 100%;
  }

  .per_box {
    display: flex;
    justify-content: center;
    margin: ${vw(20, 'mobile')} 0 ${vw(20, 'mobile')} auto;
    min-width: ${vw(125, 'mobile')};
    text-align: left;

    ${mq.greaterThan('nexus7')} {
      margin: ${vw(20, 'tablet')} 0 ${vw(20, 'tablet')} auto;
      min-width: ${vw(125, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin: ${vw(20, 'desktop')} 0 ${vw(20, 'desktop')} auto;
      min-width: ${vw(180, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin: 20px 0 20px auto;
      min-width: 180px;
    }

    > span {
      ${presets.font15_1B}
      ${createUnderline({ height: 1, color: '--color-text', reverse: true })}
    }
  }
`

const FlexWrap = styled.div<{ $isPrivate: boolean }>`
  align-items: stretch;
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $isPrivate }) => $isPrivate ? 'space-between' : 'flex-end'};
  padding: 0 0 ${vw(35, 'mobile')};
  row-gap: ${vw(20, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    padding: 0 0 ${vw(35, 'tablet')};
    row-gap: ${vw(20, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    padding: 0 0 ${vw(25, 'desktop')};
    row-gap: ${vw(20, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    padding: 0 0 25px;
    row-gap: 20px;
  }

  button {
    ${presets.font12_1B}
  }

  > p {
    padding-top: 8px;
  }
`

const MediaWrapper = styled.div`
  position: relative;
`

// const LabelWrapper = styled.div`
//   display: flex;
//   position: absolute;
//   top: 8px;
//   left: 0;

//   span {
//     ${presets.font12_1B}
//     background-color: var(--color-text--darker);
//     border-radius: ${vw(12, 'mobile')};
//     color: var(--color-white);
//     margin-right: ${vw(5, 'mobile')};
//     padding: ${vw(5, 'mobile')} ${vw(8, 'mobile')};
//     text-transform: uppercase;
//     z-index: 1;

//     ${mq.greaterThan('nexus7')} {
//       border-radius: ${vw(12, 'tablet')};
//       margin-right: ${vw(5, 'tablet')};
//       padding: ${vw(5, 'tablet')} ${vw(8, 'tablet')};
//     }

//     ${mq.greaterThan('tablet')} {
//       border-radius: ${vw(12, 'desktop')};
//       margin-right: ${vw(5, 'desktop')};
//       padding: ${vw(5, 'desktop')} ${vw(8, 'desktop')};
//     }

//     ${mq.greaterThan('desktop')} {
//       border-radius: 12px;
//       margin-right: 5px;
//       padding: 5px 8px;
//     }
//   }

//   .fresh {
//     text-transform: none;
//   }

//   .discount {
//     background-color: var(--color-text);
//   }

//   .filter-label {
//     background-color: unset;
//     height: ${vw(24, 'mobile')};
//     margin-right: ${vw(5, 'mobile')};
//     padding: 0;
//     width: ${vw(24, 'mobile')};
//     z-index: 1;

//     ${mq.greaterThan('nexus7')} {
//       height: ${vw(24, 'tablet')};
//       margin-right: ${vw(5, 'tablet')};
//       width: ${vw(24, 'tablet')};
//     }

//     ${mq.greaterThan('tablet')} {
//       height: ${vw(24, 'desktop')};
//       margin-right: ${vw(5, 'desktop')};
//       width: ${vw(24, 'desktop')};
//     }

//     ${mq.greaterThan('desktop')} {
//       height: 24px;
//       margin-right: 5px;
//       width: 24px;
//     }

//     > figure,
//     > img {
//       height: ${vw(24, 'mobile')};
//       width: ${vw(24, 'mobile')};

//       ${mq.greaterThan('nexus7')} {
//         height: ${vw(24, 'tablet')};
//         width: ${vw(24, 'tablet')};
//       }

//       ${mq.greaterThan('tablet')} {
//         height: ${vw(24, 'desktop')};
//         width: ${vw(24, 'desktop')};
//       }

//       ${mq.greaterThan('desktop')} {
//         height: 24px;
//         width: 24px;
//       }
//     }
//   }
// `

const SpanDeliveryDate = styled.span`
  ${presets.font15}
  font-family: var(--font-graphik);
  display: block;
  opacity: .65;
  text-align: right;
  width: 100%;
`

export const getFilterLabels = (
  filters: { param: string }[],
  pathname: string,
  showTooltip?: boolean,
  setShowTooltip?: Dispatch<SetStateAction<boolean>>
) => filters.map((filter, idx) => {
  const isPDP =
    pathname === '/catalog/product/[slug]' ||
    pathname === '/shop/product/[slug]'
  return (
    <figure
      key={idx}
      {...(filter.param === 'fresh' && {
        onMouseEnter: () => {
          setShowTooltip(true)
        },
        onMouseLeave: () => {
          setShowTooltip(false)
        },
      })}
      className={`filter-label ${filter.param === 'fresh' ? 'fresh' : ''}`}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={FILTER_LABELS[filter.param]} alt='Filter label image' />
      {filter.param === 'fresh' && isPDP ? (
        <ToolTip showTooltip={showTooltip} />
      ) : (
        <></>
      )}
    </figure>
  )
})
